import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { fromEvent } from 'rxjs';

import { NumberPipe } from '../../pipes/number.pipe';
import { AppIcon } from '../icons/icon.type';
import { AppIconsLibrary } from '../icons/icons-library.service';
import { InputComponent } from './input.component';

@Component({
  selector: 'app-number-input',
  template: `
    <input class="inp" type="text" [attr.placeholder]="placeholder" [attr.name]="name" inputmode="decimal" #input />
    <ng-content></ng-content>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberInputComponent),
      multi: true,
    },
  ],
  styleUrls: ['./styles/input.scss', './styles/number-input.scss', './styles/input-validation.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberInputComponent extends InputComponent implements OnInit, OnChanges {
  @Input()
  set step(s: number | undefined) {
    this.stepNumber = s || 1;
  }
  get step(): number | undefined {
    return this.stepNumber;
  }

  @Input()
  @HostBinding('attr.appearance')
  appearance = true;

  @Input()
  decimals = 0;

  @Input()
  min?: number;

  @Input()
  max?: number;

  // TODO: implement ' ' divider logic
  @Input()
  kDivider?: ' ' | '.';

  @Input()
  icons?: [AppIcon, AppIcon];

  @Output()
  appearanceClick = new EventEmitter<{ new: string; previous: string }>();

  private stepNumber = 1;

  // used to set value from FromControl
  // while current field is active writeValue() will be pending
  // and changed after blur occurred
  // this would be helpful to set min value on blur if user input value less then min
  private pendingValue: number | null = null;
  private pipe = new NumberPipe();
  private addEl?: HTMLElement;
  private valueNumber?: number;
  private subEl?: HTMLElement;

  constructor(
    renderer: Renderer2,
    elementRef: ElementRef,
    private appIcons: AppIconsLibrary,
  ) {
    super(elementRef, renderer);
    this.renderer.addClass(elementRef.nativeElement, 'number');
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.appearance) {
      this.createNumberAppearance();
    }
  }

  override ngOnChanges(changes: SimpleChanges<NumberInputComponent>): void {
    super.ngOnChanges(changes);
    if (changes.appearance) {
      if (changes.appearance.currentValue === true) {
        this.createNumberAppearance();
      } else if (this.addEl && this.subEl) {
        this.renderer.removeChild(this.elementRef.nativeElement, this.addEl);
        this.renderer.removeChild(this.elementRef.nativeElement, this.subEl);
      }
    }
  }

  protected override onInput(e: InputEvent): void {
    let value = this.getInputValue();
    if (e.data && e.data.length > 1) {
      value = e.data.replace(/[.]/g, '');
    }
    let caret = this.inputRef.nativeElement.selectionStart || 0;
    const tmpLength = value.length;
    if (!this.kDivider) {
      value = value.replace(/[^0-9]/g, '');
      caret += value.length - tmpLength;
      this.inputRef.nativeElement.setSelectionRange(caret, caret);
      this.setInputValue(value);
      this.onControlChanged(+value);
      return;
    }
    let updateCaret = true;
    value = value.replace(/[^0-9.,]/g, '');
    if (isFinite(+(e.data || NaN)) && e.data?.length === 1 && caret === value.length - 2) {
      value = replaceCharAt(value, value.length - 2, value[value.length - 1]);
    }
    if (value.length === 1 && e.inputType !== 'deleteContentBackward') {
      caret = 1;
      updateCaret = false;
    }
    if (e.inputType === 'deleteContentBackward' && caret === value.length - 1) {
      const tmpValue = value[caret];
      value = replaceCharAt(value, caret, '0');
      value = replaceCharAt(value, caret + 1, tmpValue);
    }
    if (e.inputType === 'deleteContentBackward' && (this.valueNumber || 0) < 1 && !caret) {
      caret = 0;
      updateCaret = false;
    }
    if (e.inputType === 'deleteContentBackward' && !value.includes(',')) {
      value = formatWithDecimalDelimiter(value);
    }
    if (value.split(',')[1]?.length === 3) {
      caret++;
    }
    if (value.split(',').length === 3 && !value.includes(',,') && value[caret - 1] === ',') {
      value = removeCharAt(value, caret - 1);
    }
    if (value.includes(',,') || value.includes('.,') || value.includes('..')) {
      caret++;
    }
    value = formatNumber(+value.replace(/[.]/g, '').replace(',', '.').replace(',', ''), this.kDivider, this.decimals);
    let valueNumber = +value.replace(/[.]/g, '').replace(',', '.');
    if (this.min && valueNumber < this.min) {
      valueNumber = this.min;
      value = formatNumber(valueNumber, this.kDivider, this.decimals);
      valueNumber = +value.replace(/[.]/g, '').replace(',', '.');
    } else if (this.max && valueNumber > this.max) {
      valueNumber = this.max;
      value = formatNumber(valueNumber, this.kDivider, this.decimals);
      valueNumber = +value.replace(/[.]/g, '').replace(',', '.');
    }
    this.setInputValue(value);
    if (updateCaret) {
      caret += value.length - tmpLength;
      if (e.inputType === 'deleteContentBackward' && value.length - tmpLength === 1) {
        caret -= 1;
        caret = caret === 0 ? 1 : caret;
      }
    }
    this.inputRef.nativeElement.setSelectionRange(caret, caret);
    if (this.valueNumber !== valueNumber) {
      this.valueNumber = valueNumber;
      this.onControlChanged(valueNumber);
    }
  }

  protected override setInputValue(v: string): void {
    super.setInputValue(v);
  }

  protected override onBlur(): void {
    if (this.pendingValue) {
      if (this.kDivider) {
        this.setInputValue(formatNumber(this.pendingValue, this.kDivider, this.decimals));
      } else {
        this.setInputValue(this.toFixed(this.pendingValue));
      }
      this.pendingValue = null;
    }
    super.onBlur();
  }

  private createNumberAppearance(): void {
    this.addEl = this.renderer.createElement('span') as HTMLElement;
    this.subEl = this.renderer.createElement('span') as HTMLElement;
    const minusIcon = this.appIcons.getIcon('minus') || this.icons?.[0];
    const plusIcon = this.appIcons.getIcon('plus') || this.icons?.[1];
    if (plusIcon && minusIcon) {
      this.renderer.setProperty(this.addEl, 'innerHTML', plusIcon.svgData);
      this.renderer.setProperty(this.subEl, 'innerHTML', minusIcon.svgData);
    } else {
      this.renderer.setProperty(this.addEl, 'innerText', '+');
      this.renderer.setProperty(this.subEl, 'innerText', '-');
    }

    this.renderer.addClass(this.addEl, 'plus');
    this.renderer.addClass(this.subEl, 'minus');
    this.renderer.appendChild(this.elementRef.nativeElement, this.addEl);
    this.renderer.appendChild(this.elementRef.nativeElement, this.subEl);
    fromEvent<MouseEvent>(this.addEl, 'click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(e => {
        e.preventDefault();
        this.increase();
      });
    fromEvent<MouseEvent>(this.subEl, 'click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(e => {
        e.preventDefault();
        this.decrease();
      });
  }

  increase() {
    const tmpValue = this.getInputValue();
    this.changeNumber(1);
    this.appearanceClick.next({ new: this.getInputValue(), previous: tmpValue });
  }

  decrease() {
    const tmpValue = this.getInputValue();
    this.changeNumber(-1);
    this.appearanceClick.next({ new: this.getInputValue(), previous: tmpValue });
  }

  private changeNumber(sign: 1 | -1): void {
    const strValue = this.getInputValue();
    if (!strValue) {
      this.setInputValue(this.toFixed(0));
    } else {
      const value = parseFloat(strValue);
      this.setInputValue(Math.max(0, value + (this.step || 1) * sign).toFixed(this.decimals));
    }
    this.onControlChanged(+this.getInputValue());
    this.onControlTouched();
  }

  private toFixed(value: number, decimals = this.decimals): string {
    return this.pipe.transform(value, decimals || 0);
  }

  override writeValue(value: string | number | object | undefined): void {
    if(!value) {
      this.setInputValue('');
      return;
    }
    if (this.active) {
      this.pendingValue = +value;
    } else {
      if (value === 0) {
        value = '0';
      }
      if (!this.kDivider) {
        this.setInputValue(value.toString());
      } else {
        this.setInputValue(value ? formatNumber(+value, this.kDivider, this.decimals) : '');
      }
    }
  }
}

function formatNumber(value: number, kDivider: ' ' | '.', decimals: number): string {
  if (value === 0) {
    return '';
  }
  const useCommaForDecimal = kDivider === '.';
  const decimalSeparator = useCommaForDecimal ? ',' : '.';
  const thousandsSeparator = useCommaForDecimal ? '.' : ' ';

  const fixed = truncateDecimals(value, decimals);
  const [integerPart, decimalPart] = fixed.split('.');

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  return decimalPart ? `${formattedInteger}${decimalSeparator}${decimalPart}` : formattedInteger;
}

function formatWithDecimalDelimiter(str: string): string {
  if (str.length <= 2) {
    return '0.' + str.padStart(2, '0');
  }
  return str.slice(0, -2) + ',' + str.slice(-2);
}

function removeCharAt(str: string, index: number): string {
  if (index < 0 || index >= str.length) return str; // index out of bounds
  return str.slice(0, index) + str.slice(index + 1);
}

function truncateDecimals(value: number, decimals: number): string {
  const factor = Math.pow(10, decimals);
  return (Math.floor(value * factor) / factor).toFixed(decimals);
}

function replaceCharAt(str: string, index: number, replacement: string): string {
  if (index < 0) return str; // ignore negative indexes
  if (index < str.length) {
    return str.substring(0, index) + replacement + str.substring(index + 1);
  } else {
    return str + replacement;
  }
}
